import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import clsx from 'clsx';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import downArrow from '@/assets/Icons/Chevron Down.svg';
import stethoscope from '@/assets/Icons/stethoscope.svg';

import { Context as AuthContext } from '@/context/AuthContext.js';

import { isXundEnabled } from '@/service/Xund/xundService.js';

import { onBandColor } from '@/styles/darkHero.ts';

import { SELECT_MENU_PROPS } from '@/theme/index.ts';

import { getTextColor } from '@/utils/colorHelpers.js';
import { HealthInsurance } from '@/utils/constants.js';
import { hasCategoryBrowser } from '@/utils/customer-view-utils.ts';
import { useQuery } from '@/utils/helpers.js';

import { CLEARED_CHAIN_SELECTION, useConfigStore, useSearchStore, useXundStore } from '@/store.js';

const SelectChevron = (props) => <img {...props} src={downArrow} alt="" />;

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '40rem',
        margin: '0 auto',
        gap: '1rem',
        boxSizing: 'border-box',
        marginBottom: '2rem',
    },
    topRow: {
        display: 'flex',
        alignItems: 'end',
        gap: '20px',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    insuranceRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            gap: '0.5rem',
        },
    },
    insuranceOption: {
        padding: '0.5rem 0.875rem',
        borderRadius: theme.radius.field,
        border: `1px solid ${theme.palette.common.yellow}`,
        background: theme.palette.common.white,
        color: theme.palette.common.darkGrey,
        fontSize: '0.875rem',
        fontWeight: theme.weight.body,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 200ms ease',
        '&:hover': {
            background: theme.palette.common.lightBrown,
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.common.darkGrey}`,
            outlineOffset: '1px',
        },
        [theme.breakpoints.down('sm')]: {
            padding: '8px 10px',
            fontSize: '0.875rem',
        },
    },
    insuranceOptionSelected: {
        background: theme.palette.common.yellow,
        color: getTextColor(theme.palette.common.yellow, theme.palette.common.darkGrey, theme.palette.common.white),
        fontWeight: theme.weight.heading,
        '&:hover': {
            background: theme.palette.common.hoverYellow,
        },
    },
    elementContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    label: {
        fontSize: 15,
        margin: 0,
        color: onBandColor(theme),
    },
    formControl: {
        padding: '12px 16px',
        borderRadius: theme.radius.field,
        border: '1px solid rgba(46, 46, 46, 0.70)',
        fontSize: '16px',
        outline: 'none',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        color: 'black',
        background: 'white',
    },
    searchInput: {
        padding: '12px 32px 12px 44px',
    },
    searchIcon: {
        position: 'absolute',
        left: '0.75rem',
        top: '50%',
        transform: 'translate(0, -50%)',
        color: theme.palette.common.midGrey,
        pointerEvents: 'none',
    },
    searchWrapper: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    clearIcon: {
        position: 'absolute',
        right: '0.5rem',
        top: '50%',
        transform: 'translate(0, -50%)',
        cursor: 'pointer',
        transition: 'opacity 0.3s ease',
    },
    locationSelectInput: {
        padding: 0,
        paddingRight: '1.5rem',
        background: 'transparent',
        '&:focus': {
            background: 'transparent',
        },
    },
    locationSelectIcon: {
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: 'translate(0, -50%)',
        pointerEvents: 'none',
    },
    locationMenuPaper: {
        boxShadow: 'none',
        border: '1px solid rgba(46, 46, 46, 0.70)',
        borderRadius: theme.radius.field,
    },
    triageButton: {
        padding: '12px 16px',
        borderRadius: theme.buttonShape.radius,
        border: 'none',
        outline: 'none',
        fontSize: '16px',
        fontWeight: theme.weight.heading,
        cursor: 'pointer',
        backgroundColor: theme.palette.common.yellow,
        color: 'black',
        minWidth: '140px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        '&:hover': {
            backgroundColor: theme.palette.common.yellow,
            opacity: 0.9,
        },
        '&:focus': {
            outline: 'none',
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            marginTop: '1rem',
        },
    },
}));

const SearchBar = ({ onXundToggle, showXund }) => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const history = useHistory();
    const query = useQuery();
    const {
        state: { userHealthInsurance },
    } = useContext(AuthContext);
    const prismicData = useSearchStore((state) => state.prismicData);
    const search = useSearchStore((state) => state.search);
    const selectedHealthInsurance = useSearchStore((state) => state.selectedHealthInsurance);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const selectedProfessional = useSearchStore((state) => state.selectedProfessional);
    const treatments = useSearchStore((state) => state.treatments);
    const specialties = useSearchStore((state) => state.specialties);
    const queryValuesSet = useSearchStore((state) => state.queryValuesSet);
    const currentCustomer = useConfigStore((state) => state.currentCustomer);
    const customerId = currentCustomer?.customer_id?.[0].text;
    const [insuranceOptions, setInsuranceOptions] = useState(Object.values(HealthInsurance));

    const getSelectedHealthInsurance = (availableInsuranceTypes) => {
        const queryInsurance = query.get('insurance_type');

        if (queryInsurance) {
            return availableInsuranceTypes.includes(HealthInsurance[queryInsurance.toUpperCase()])
                ? HealthInsurance[queryInsurance.toUpperCase()]
                : availableInsuranceTypes[0];
        }

        if (
            (hasCategoryBrowser(currentCustomer) && !currentCustomer?.is_accordion_for_multiple_professionals) ||
            !userHealthInsurance?.insurance_type
        ) {
            return currentCustomer.preselected_insurance ?? availableInsuranceTypes[0];
        }

        if (
            userHealthInsurance.insurance_type === 'SELFPAYER' &&
            availableInsuranceTypes.includes(HealthInsurance.SELF_PAYER)
        ) {
            return 'self_payer';
        }
        return availableInsuranceTypes.includes(userHealthInsurance.insurance_type.toLowerCase())
            ? userHealthInsurance.insurance_type.toLowerCase()
            : availableInsuranceTypes[0];
    };

    useEffect(() => {
        if (!treatments[selectedLocation]) return;

        const insuranceFlags = {
            isPublicAvailable: false,
            isPrivateAvailable: false,
            isSelfPayerAvailable: false,
        };

        for (const treatment of Object.values(treatments[selectedLocation])) {
            if (treatment.public_insurance) insuranceFlags.isPublicAvailable = true;
            if (treatment.private_insurance) insuranceFlags.isPrivateAvailable = true;
            if (treatment.self_payer) insuranceFlags.isSelfPayerAvailable = true;

            // Break out early if all flags are true
            if (
                insuranceFlags.isPublicAvailable &&
                insuranceFlags.isPrivateAvailable &&
                insuranceFlags.isSelfPayerAvailable
            ) {
                break;
            }
        }

        const availableInsuranceTypes = [];
        if (insuranceFlags.isPublicAvailable) availableInsuranceTypes.push(HealthInsurance.PUBLIC);
        if (insuranceFlags.isPrivateAvailable) availableInsuranceTypes.push(HealthInsurance.PRIVATE);
        if (insuranceFlags.isSelfPayerAvailable) availableInsuranceTypes.push(HealthInsurance.SELF_PAYER);

        setInsuranceOptions(availableInsuranceTypes);

        useSearchStore.setState({
            selectedHealthInsurance: getSelectedHealthInsurance(availableInsuranceTypes),
        });
    }, [treatments, selectedLocation]);

    useEffect(() => {
        if (queryValuesSet) return;

        const querySearch = query.get('search');
        const querySpecialty = query.get('specialty');
        const querySelectedProfessional = query.get('selected_professional');
        const queryLocation = query.get('location');
        const queryTreatment = query.get('service_type');
        const queryIsOnlineService = query.get('is_online_service');

        const doesQueryLocationExist =
            queryLocation && currentCustomer.locations.some((entry) => entry.key[0]?.text === queryLocation);
        const newSelectedLocation = doesQueryLocationExist
            ? queryLocation
            : (selectedLocation ?? currentCustomer.locations[0]?.key[0].text);
        const newSelectedTreatment = treatments[newSelectedLocation]?.[queryTreatment];
        const isOnlineService = queryIsOnlineService === 'true';

        const getSearch = () => {
            if (newSelectedTreatment) return newSelectedTreatment.name[0].text;

            if (querySpecialty && specialties[decodeURI(querySpecialty)]) {
                return specialties[decodeURI(querySpecialty)].name[0].text;
            }

            if (querySearch) return decodeURI(querySearch);

            return '';
        };

        useSearchStore.setState({
            search: getSearch(),
            selectedSpecialty: querySpecialty,
            selectedLocation: newSelectedLocation,
            selectedProfessional: querySelectedProfessional,
            selectedTreatments: newSelectedTreatment
                ? {
                      [queryTreatment]: true,
                  }
                : {},
            ...CLEARED_CHAIN_SELECTION,
            showAvailableProfessionals: !!newSelectedTreatment,
            isOnlineAppointment: !!newSelectedTreatment && isOnlineService,
            queryValuesSet: true,
        });
    }, []);

    const updateQueryParams = () => {
        history.replace({ search: query.toString() });
    };

    const updateSearch = (value) => {
        useSearchStore.setState({
            search: value,
            selectedSpecialty: null,
            ...CLEARED_CHAIN_SELECTION,
            showAvailableProfessionals: false,
            selectedProfessional: null,
        });
        query.set('search', encodeURI(value));
        query.delete('specialty');
        updateQueryParams();

        useXundStore.setState({
            isXundTriageCompleted: false,
            isXundTriageProcessed: false,
        });
    };

    const updateHealthInsurance = (value) => {
        useSearchStore.setState({
            selectedHealthInsurance: value,
            selectedTreatments: {},
            ...CLEARED_CHAIN_SELECTION,
            showAvailableProfessionals: false,
        });
        query.set('insurance_type', encodeURI(value));
        updateQueryParams();
    };

    const updateSelectedLocation = (event) => {
        useSearchStore.setState({
            selectedLocation: event.target.value,
            selectedTreatments: {},
            ...CLEARED_CHAIN_SELECTION,
            showAvailableProfessionals: false,
            selectedSpecialty: null,
            search: '',
        });
        query.delete('specialty');
        query.delete('search');
        updateQueryParams();
    };

    const searchInputComponent = useMemo(
        () => (
            <div className={classes.elementContainer} style={{ flex: 2 }}>
                <p className={classes.label}>{prismicData.search_input_title[0].text}</p>
                <div className={classes.searchWrapper}>
                    <SearchIcon className={classes.searchIcon} fontSize="small" />
                    <input
                        data-testid="search-input"
                        autoComplete="off"
                        value={search}
                        onChange={(e) => updateSearch(e.target.value)}
                        className={clsx(classes.formControl, classes.searchInput)}
                        placeholder={
                            hasCategoryBrowser(currentCustomer)
                                ? prismicData.accordion_search_input_placeholder[0].text
                                : prismicData.search_input_placeholder[0].text
                        }
                        type="text"
                        disabled={showXund}
                    />
                    <svg
                        data-testid="search-clear-btn"
                        className={classes.clearIcon}
                        style={{
                            opacity: search ? 1 : 0,
                            pointerEvents: search ? 'auto' : 'none',
                        }}
                        onClick={() => {
                            history.replace({ search: '' });
                            useSearchStore.setState({
                                ...CLEARED_CHAIN_SELECTION,
                                showAvailableProfessionals: false,
                                selectedProfessional: null,
                                search: '',
                            });
                        }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M16.6875 15.3125C17.0938 15.6875 17.0938 16.3438 16.6875 16.7188C16.5 16.9062 16.25 17 16 17C15.7188 17 15.4688 16.9062 15.2812 16.7188L12 13.4375L8.6875 16.7188C8.5 16.9062 8.25 17 8 17C7.71875 17 7.46875 16.9062 7.28125 16.7188C6.875 16.3438 6.875 15.6875 7.28125 15.3125L10.5625 12L7.28125 8.71875C6.875 8.34375 6.875 7.6875 7.28125 7.3125C7.65625 6.90625 8.3125 6.90625 8.6875 7.3125L12 10.5938L15.2812 7.3125C15.6562 6.90625 16.3125 6.90625 16.6875 7.3125C17.0938 7.6875 17.0938 8.34375 16.6875 8.71875L13.4062 12.0312L16.6875 15.3125Z"
                            fill="#2E2E2E"
                        />
                    </svg>
                </div>
            </div>
        ),
        [classes, prismicData, search, currentCustomer, history, showXund]
    );

    const insuranceOptionsComponent = useMemo(
        () => (
            <div className={classes.insuranceRow} data-testid="search-insurance-options">
                {insuranceOptions.map((healthInsurance) => (
                    <button
                        key={healthInsurance}
                        type="button"
                        aria-pressed={selectedHealthInsurance === healthInsurance}
                        onClick={() => updateHealthInsurance(healthInsurance)}
                        className={clsx(
                            classes.insuranceOption,
                            selectedHealthInsurance === healthInsurance && classes.insuranceOptionSelected
                        )}
                    >
                        {t(healthInsurance)}
                    </button>
                ))}
            </div>
        ),
        [classes, selectedHealthInsurance, insuranceOptions, i18n.language]
    );

    const locationDropdownComponent = useMemo(
        () =>
            currentCustomer.locations.length > 1 ? (
                <div className={classes.elementContainer}>
                    <p className={classes.label}>{prismicData.search_location_title[0].text}</p>
                    <Select
                        disableUnderline
                        data-testid="search-location-select"
                        value={selectedLocation}
                        onChange={updateSelectedLocation}
                        disabled={!!selectedProfessional}
                        className={classes.formControl}
                        classes={{ select: classes.locationSelectInput, icon: classes.locationSelectIcon }}
                        IconComponent={SelectChevron}
                        style={{
                            width: '100%',
                            opacity: selectedProfessional ? 0.5 : 1,
                            cursor: selectedProfessional ? 'not-allowed' : 'unset',
                        }}
                        MenuProps={{
                            ...SELECT_MENU_PROPS,
                            elevation: 0,
                            MenuListProps: { disablePadding: true },
                            PaperProps: { className: classes.locationMenuPaper },
                        }}
                    >
                        {currentCustomer.locations.map((location) => (
                            <MenuItem key={location.key[0].text} value={location.key[0].text}>
                                {location.title[0].text}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            ) : null,
        [classes, currentCustomer, prismicData, selectedProfessional, selectedLocation]
    );

    const triageButtonText = showXund ? t('EndTriage') : t('StartTriage');

    return (
        <div className={classes.container}>
            <div className={classes.topRow}>
                {searchInputComponent}
                {locationDropdownComponent}
                {isXundEnabled(selectedLocation, customerId) && (
                    <button data-testid="search-triage-btn" className={classes.triageButton} onClick={onXundToggle}>
                        <img src={stethoscope} alt="stethoscope" style={{ width: '20px', height: '20px' }} />
                        {triageButtonText}
                    </button>
                )}
            </div>
            {insuranceOptionsComponent}
        </div>
    );
};

export default SearchBar;
